import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';
import Axios from 'axios';
import { FORM_ERROR } from 'final-form';
import PasswordRequirements from '@/components/shared/PasswordRequirements';
import { strictPasswordValidationError } from '@/components/shared/PasswordRequirements/helpers';
import SubmitButton from '../../../common/Forms/SubmitButton';
import { snakeToTitle } from '../../../common/Utils';
import { renderErrors, SubmitError } from '../../../common/Forms/Utils';
import {
  required, minLength, composeValidators, validEmail
} from '../../../common/Forms/Validators';
import ContactAdminButton from '../../registrations/ContactAdminButton';
import showToast from '../../../common/Toast';
import TransferModal from '../../registrations/TransferModal';
import Text from '../../../ui/Text';
import cn from '../../../../utils/cn';
import styles from './MyAccount.module.scss';
import MyAccountDropdown from './MyAccountDropdown';

export default class MyAccount extends Component {
  static propTypes = {
    active: PropTypes.bool,
    getAdminInfoPath: PropTypes.string.isRequired,
    settings: PropTypes.shape({
      default_home: PropTypes.string,
      program_homepage: PropTypes.string
    }).isRequired,
    signUpCodesPath: PropTypes.string.isRequired,
    staffer: PropTypes.shape({
      email: PropTypes.string.isRequired,
      first_name: PropTypes.string.isRequired,
      from_auto_roster_district: PropTypes.bool.isRequired,
      has_strict_password_requirement: PropTypes.bool.isRequired,
      last_name: PropTypes.string.isRequired,
      username: PropTypes.string
    }).isRequired,
    subscriberContactAdminMessage: PropTypes.string,
    subscriberName: PropTypes.string.isRequired,
    transferPath: PropTypes.string.isRequired,
    updatePath: PropTypes.string.isRequired,
    updateState: PropTypes.func,
    userType: PropTypes.string.isRequired
  };

  static defaultProps = {
    active: false,
    updateState: () => {}
  };

  static _renderFieldLabel(attribute) {
    const titleizedAttribute = snakeToTitle(attribute);

    return (
      <span>{titleizedAttribute}</span>
    );
  }

  constructor(props) {
    super(props);

    this.state = {
      submitting: false
    };
  }

  handleSubmit = async (values) => {
    this.setState({ submitting: true });

    const newValues = {
      ...values,
      default_home: values?.default_home?.value,
    };

    return Axios
      .put(this.props.updatePath, newValues)
      .then((response) => {
        showToast('Save successful');
        this.props.updateState({
          staffer: response.data.staffer,
          settings: response.data.data.settings
        });
        this.setState({ submitting: false });
      })
      .catch(error => this._handleSubmitError(error));
  };

  _handleSubmitError(error) {
    this.setState({ submitting: false });
    return { [FORM_ERROR]: renderErrors(error.response.data.errors) };
  }

  _getInitialValues() {
    const {
      email, first_name, last_name, username
    } = this.props.staffer;

    const {
      default_home, program_homepage
    } = this.props.settings;

    return {
      default_home, email, first_name, last_name, program_homepage, username
    };
  }

  _renderField(attribute, validator) {
    if (this.props.staffer.from_auto_roster_district) {
      return (
        <div className={styles.row}>
          <span className={styles.label}>
            {MyAccount._renderFieldLabel(attribute)}
          </span>
          {this.props.staffer[attribute]}
        </div>
      );
    }

    return (
      <Field
        name={attribute}
        validate={validator}
      >
        {({ input, meta }) => (
          <div className={styles.row}>
            <label className={styles.label} htmlFor={`staff-${attribute.replace(/_/g, '-')}`}>
              <Text size="sm" variant="label">
                {MyAccount._renderFieldLabel(attribute)}
              </Text>
            </label>

            <div>
              <Text asChild className="tw-p-3" size="sm">
                <input
                  className={styles.textInput}
                  id={`staff-${attribute.replace(/_/g, '-')}`}
                  {...input}
                />
              </Text>

              {meta.error && meta.touched &&
              <div className={styles.fieldError}>{meta.error}</div>}
            </div>
          </div>
        )}
      </Field>
    );
  }

  _renderPasswordFields(values) {
    if (this.props.staffer.from_auto_roster_district) return null;
    const hasStrictPasswordRequirement = this.props.staffer.has_strict_password_requirement;

    return (
      <div>
        <Field
          name="password"
          validate={hasStrictPasswordRequirement ? strictPasswordValidationError : minLength(6)}
        >
          {({ input, meta }) => (
            <>
              <div className={styles.row}>
                <Text asChild size="sm" variant="label">
                  <label className={styles.label} htmlFor="staff-password">
                    New Password
                  </label>
                </Text>

                <div>
                  <input
                    autoComplete="new-password"
                    className={styles.textInput}
                    id="staff-password"
                    type="password"
                    {...input}
                  />

                  {!hasStrictPasswordRequirement && meta.error && meta.touched && (
                    <div className={styles.fieldError}>
                      {meta.error}
                    </div>
                  )}
                </div>

              </div>
              {hasStrictPasswordRequirement && (
                <PasswordRequirements
                  maxWidth={290}
                  password={values.password || ''}
                  spacerWidth={200}
                />
              )}
            </>
          )}
        </Field>

        <Field
          name="password_confirmation"
          validate={minLength(6)}
        >
          {({ input, meta }) => (
            <div className={styles.row}>
              <Text asChild size="sm" variant="label">
                <label className={styles.label} htmlFor="staff-password-confirmation">
                  Confirm New Password
                </label>
              </Text>

              <div>
                <input
                  autoComplete="new-password"
                  className={styles.textInput}
                  id="staff-password-confirmation"
                  type="password"
                  {...input}
                />

                {meta.error && meta.touched &&
                <div className={styles.fieldError}>{meta.error}</div>}
              </div>
            </div>
          )}
        </Field>
        <hr />
        <p className="mb20">
          <Text size="sm" weight="bold">
            Your current password is required for all changes to your account settings.
          </Text>
        </p>
        <Field
          name="current_password"
          validate={composeValidators(required, minLength(6))}
        >
          {({ input, meta }) => (
            <div className={styles.row}>
              <Text asChild size="sm" variant="label">
                <label className={styles.label} htmlFor="staff-current-password">
                  Current Password
                </label>
              </Text>

              <div>
                <input
                  autoComplete="new-password"
                  className={styles.textInput}
                  id="staff-current-password"
                  type="password"
                  {...input}
                />

                {meta.error && meta.touched &&
                <div className={styles.fieldError}>{meta.error}</div>}
              </div>
            </div>
          )}
        </Field>
      </div>
    );
  }

  _renderContactInfo() {
    if (!this.props.staffer.from_auto_roster_district) return null;

    return (
      <div className={`mb20 ${styles.info}`}>
        <span className={styles.content}>
          <i aria-hidden="true" className={`fa fa-info-circle ${styles.icon}`} />
          <p>
            Your district manages all account information.
            To request a change, please contact your district administrator.
          </p>
        </span>
        <div className={styles.buttonContainer}>
          <ContactAdminButton
            buttonClass="btn--outline-purple"
            getAdminInfoPath={this.props.getAdminInfoPath}
            subscriberContactAdminMessage={this.props.subscriberContactAdminMessage}
          />
        </div>
      </div>
    );
  }

  render() {
    if (!this.props.active) return null;

    return (
      <div>
        {this._renderContactInfo()}
        <Form
          initialValues={this._getInitialValues()}
          render={({
            dirtySinceLastSubmit, handleSubmit, hasSubmitErrors,
            hasValidationErrors, pristine, submitError, values
          }) => (
            <form onSubmit={handleSubmit}>
              {this._renderField('first_name', required)}
              {this._renderField('last_name', required)}
              {this._renderField('email', composeValidators(required, validEmail))}
              {this._renderField('username')}
              <Text className={styles.fieldAnnotation} size="sm">
                Username is optional. Students can use your email or username to sign in.
              </Text>
              <div className={cn(styles.row, 'tw-mt-5')}>
                <Text className={styles.label} size="sm" variant="label">
                  Role
                </Text>
                <Text className={cn(styles.label)} size="sm">
                  {this.props.userType}
                </Text>
              </div>
              <div className={styles.row}>
                <Text className={styles.label} size="sm" variant="label">District Account</Text>
                <TransferModal
                  signUpCodesPath={this.props.signUpCodesPath}
                  subscriberName={this.props.subscriberName}
                  transferPath={this.props.transferPath}
                />
              </div>
              {!this.props.staffer.from_auto_roster_district && <hr />}
              {this._renderPasswordFields(values)}
              <SubmitError error={submitError} />
              {(!this.props.staffer.from_auto_roster_district &&
                (this.props.userType === 'Coordinator' || this.props.userType === 'Admin')) && (
                <>
                  <hr />
                  <MyAccountDropdown
                    className={styles.row}
                    label="Default Landing Page"
                    name="default_home"
                    options={[
                      { label: 'Admin Dashboard', value: '0' },
                      { label: 'My Programs', value: '1' },
                    ]}
                  />
                  <hr />
                </>
              )}
              <div className={styles.actionContainer}>
                {
                  !this.props.staffer.from_auto_roster_district && (
                    <Text asChild size="sm">
                      <SubmitButton
                        submitButtonText="Update"
                        submitDisabled={
                          pristine || hasValidationErrors || (hasSubmitErrors && !dirtySinceLastSubmit)
                        }
                        submitting={this.state.submitting}
                      />
                    </Text>
                  )
                }
              </div>
            </form>
          )}
          onSubmit={this.handleSubmit}
        />
      </div>
    );
  }
}
